import { TZDateMini } from '@date-fns/tz';
import { getDaysInMonth } from 'date-fns';
import { useMemo } from 'react';

import { getTimezone } from '@dynatrace-sdk/user-preferences';

import { useCalendarContext } from './useCalendarContext.js';
import { isTimeframeV2 } from '../../forms-core/time/types.js';
import { NUMBER_OF_MONTHS } from '../types/calendarConfiguration.js';
import type { SelectMonthViewItems } from '../views/SelectMonthView.js';

/** Custom hook used for the month selection view. */
export function useSelectMonth(): SelectMonthViewItems {
  const { state } = useCalendarContext();
  const configuredTimezone = getTimezone();

  const selectedISODate = isTimeframeV2(state.selectedDate, state.type)
    ? state.selectedDate.from?.absoluteDate
    : state.selectedDate?.absoluteDate;

  const selectedDate = useMemo(
    () =>
      new TZDateMini(
        selectedISODate || state.activeDate.toISOString(),
        configuredTimezone,
      ),
    [selectedISODate, state.activeDate, configuredTimezone],
  );

  /** Get months to display. */
  const months = useMemo(
    () =>
      Array(NUMBER_OF_MONTHS)
        .fill(0)
        .map((_, index) => {
          const activeYear = new TZDateMini(
            state.activeDate,
            configuredTimezone,
          ).getFullYear();

          const newDate = new TZDateMini(
            0,
            index,
            // Depending on the selected day/active day, the month might change to the next
            // month if there are fewer days in the month with current index.
            // e.g. active day = 31, current index = 1 (February)
            // --> new TZDateMini would end up being March 3 and we would get March twice in list of months
            // To avoid this, we have to clamp the day to the maximum number of days in given month
            Math.min(
              getDaysInMonth(
                new TZDateMini(activeYear, index, 1, configuredTimezone),
              ),
              selectedDate.getDate(),
            ),
            configuredTimezone,
          );
          // Ensure the correct year is set, as new TZDateMini(3) defaults to the year 1903
          newDate.setFullYear(activeYear);

          return newDate;
        }),
    [state.activeDate, configuredTimezone, selectedDate],
  );

  return {
    months,
  };
}
